import {
    scaleBand,
    scaleSqrt,
}                               from "d3-scale"
import { projectsLocalization } from "localization"
import React                    from "react"
import { connect }              from "react-redux"
import styled                   from "styled-components"
import Axis                     from "../common/axis/axis"
import Grid                     from "../common/axis/grid"
import Bar                      from "./bar"

const getInitialData = (data, xKey, yKey, y1Key) => {
    if (data.length < 1) {
        return []
    }

    return data.map((bar) => {
        let item = {}
        item[xKey] = bar[xKey]
        item[yKey] = 0
        item[y1Key] = 0

        return item
    })
}

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { error: null, errorInfo: null }
    }

    componentDidCatch(error, errorInfo) {
        // Catch errors in any components below and re-render with error message
        this.setState({
            error: error,
            errorInfo: errorInfo,
        })
        // You can also log error messages to an error reporting service here
    }

    render() {
        if (this.state.errorInfo) {
            // Error path
            return (
                <div>
                    <h2>Something went wrong.</h2>
                    <details style={{ whiteSpace: "pre-wrap" }}>
                        {this.state.error && this.state.error.toString()}
                        <br/>
                        {this.state.errorInfo.componentStack}
                    </details>
                </div>
            )
        }
        // Normally, just render children
        return this.props.children
    }
}

class LayeredBarChart extends React.Component {
    constructor() {
        super()
        this.state = {
            parentWidth: 0,
            tooltipData: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                data: null,
            },
        }
        this.ref = React.createRef()
    }

    componentDidMount() {
        this.fitParentContainer()
        window.addEventListener("resize", this.fitParentContainer)
    }

    fitParentContainer = () => {
        const { parentWidth } = this.state

        if (!this.ref.current) return;

        const currentParentWidth =
            this.ref.current && this.ref.current.getBoundingClientRect().width

        const shouldResize = parentWidth !== currentParentWidth
        if (shouldResize) {
            this.setState({
                parentWidth: currentParentWidth,
            })
        }
    }

    handleTooltip = tooltipData => {
        this.setState({
            tooltipData: { ...this.state.tooltipData, ...tooltipData },
        })

    }

    render() {
        const { yKey, y1Key, selectedLanguage, tooltip: ToolTip } = this.props
        let { xKey, chartData } = this.props
        const initialXkey = xKey

        if (selectedLanguage === "np") {
            xKey = initialXkey + "_in_nepali"
        }

        const { tooltipData, parentWidth } = this.state
        const initialBarData = getInitialData(chartData, xKey, yKey, y1Key)

        const projectLocalizationProcurements = projectsLocalization[selectedLanguage].procurementTypes

        chartData = chartData.map(item => {
            const str = item[initialXkey].split(" ").join("_")
            item.procurement_type_in_nepali = projectLocalizationProcurements[str]
            return item
        })

        if (parentWidth === 0) {
            return <div ref={this.ref}/>
        }

        // set the dimensions and margins of the graph
        const margin = {
            top: 10,
            right: 32,
            bottom: 60,
            left: 32,
        }

        function handleLabel(value) {
            if (selectedLanguage === "np") {
                if (Math.abs(Number(value)) >= 1.0e7) {
                    return "करोडमा"
                } else if (Math.abs(Number(value)) >= 1.0e5) {
                    return "लाखमा"
                } else if (Math.abs(Number(value)) >= 1.0e3) {
                    return "हजारमा"
                } else {
                    return "रुपैयाँमा"
                }
            } else {
                if (Math.abs(Number(value)) >= 1.0e9) {
                    return "In Billion"
                } else if (Math.abs(Number(value)) >= 1.0e6) {
                    return "In Million"
                } else if (Math.abs(Number(value)) >= 1.0e3) {
                    return "In Thousounds"
                } else {
                    return "In Rupees"
                }
            }
        }

        function findMaxBudget(chartData) {
            let budgetAmountArr = []
            chartData.forEach(budgetItem =>
                budgetAmountArr.push(budgetItem.budget),
            )
            return Math.max(...budgetAmountArr)
        }

        function Yaxis(ykey) {
            var maxBudgetAmount = chartData.reduce((a, b) => a.budget > b.budget ? a : b).budget
            const str = handleLabel(maxBudgetAmount)
            return <span className={"bar-y-label"}>{str}</span>
        }

        const width = parentWidth - margin.left - margin.right
        const height = parentWidth * 0.6 - margin.top - margin.bottom

        // creating scales
        const xScale = scaleBand()
            .domain(chartData.map(d => d[xKey]))
            .range([0, width])
            .padding(0.5)

        const yScale = scaleSqrt()
            .domain([0, Math.max(...chartData.map(d => d[yKey]))])
            .range([height, 0])

        return (
            <BarWrapper
                innerRef={this.ref}
                className="bar-chart"
                margin={margin}
                xScale={xScale}
                tooltipData={tooltipData}
            >

                <div className="tooltip">
                    <div className="tooltip-arrow bottom"/>
                    <ToolTip {...tooltipData.data}/>
                </div>

                {yKey === "budget" && <Yaxis/>}

                <svg width={width + margin.left + margin.right}
                     height={height + margin.top + margin.bottom}
                >
                    <g transform={`translate(${margin.left}, ${margin.top})`}>
                        <g className="axis-group">
                            <Axis scale={xScale} orient="bottom" transform={`translate(0, ${height})`}/>
                            <Axis highAmount={findMaxBudget(chartData)} scale={yScale} orient="left" transform={`translate(0,0)`}
                                  valueType={yKey === "budget" ? "amount" : "number"}
                                  selectedLanguage={selectedLanguage}/>
                        </g>
                        <Grid {...{ xScale, yScale, width }} />
                        <Bar
                            {...{
                                xKey,
                                yKey,
                                y1Key,
                                xScale,
                                yScale,
                                width,
                                height,
                                chartData,
                                initialBarData,
                                onMouseEnter: this.handleTooltip,
                                onClick: (datum) => (this.props.onClick(datum)),
                            }}
                        />
                    </g>
                </svg>
            </BarWrapper>
        )
    }
}

const BarWrapper = styled.div`
    position: relative;
    .tooltip
        {
            position: absolute
            visibility: ${props => props.tooltipData.left === 0 ? "hidden" : "visible"}
                left: ${props => `${props.tooltipData.left + props.margin.left}px`};
    bottom: ${props => `${props.tooltipData.bottom + props.margin.bottom + 15}px`};
    transform: ${props => `translateX(calc(-50% + ${props.xScale.bandwidth() * 0.5}px))`};
  }
  /* ******** grid ************ */
  .grid-group line {
    stroke: var(--color-grid-line);
    shape-rendering: crispEdges;
    stroke-width: 1px;
    stroke-opacity: .5;
    stroke-dasharray: 4 4;
  }
  .grid-group path {
    stroke-width: 0;
  }
   /* *********** axis  *********** */
  .axis-group path,
  .axis-group line {
    stroke-width: 0;
  }
  .left-axis text,.bottom-axis text  {
    fill: var(--color-theme-1);
    opacity: 0.5;
    font-size: 12px,;
  }
  .bottom-axis text {
    text-transform: capitalize;
  }
`

export default connect(state => ({
    selectedLanguage: state.localizationReducers.selectedLanguage,
}))(LayeredBarChart)



// WEBPACK FOOTER //
// ./src/components/charts/bar-chart/layered-bar-chart.js